import React, { useEffect, useMemo, useState } from "react";
import { api, fmtError, fmtDateTime, API } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import FileUploader from "@/components/FileUploader";
import { Plus, Trash, ImageSquare, VideoCamera, SpeakerHigh, Link as LinkIcon, X, ArrowUpRight, Code, Copy, Eye, PencilSimple, UploadSimple, DownloadSimple, Rows, SquaresFour, FileCsv } from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmDialog";
import { toast } from "sonner";
import { downloadCsv } from "@/lib/csv";

const MEDIA_TYPES = [
    { value: "image", label: "Image", Icon: ImageSquare, accept: "image/*" },
    { value: "video", label: "Video", Icon: VideoCamera, accept: "video/*" },
    { value: "audio", label: "Audio", Icon: SpeakerHigh, accept: "audio/*" },
];

export default function CreativesTab({ campaignId, canEdit }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openAdd, setOpenAdd] = useState(false);
    const [openBulk, setOpenBulk] = useState(false);
    const [editingCreative, setEditingCreative] = useState(null);
    const [previewing, setPreviewing] = useState(null);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [viewMode, setViewMode] = useState(() => {
        return localStorage.getItem("pulse.creatives.view") || "table";
    });
    const [zipBusy, setZipBusy] = useState(false);
    const confirm = useConfirm();

    useEffect(() => { localStorage.setItem("pulse.creatives.view", viewMode); }, [viewMode]);

    // Prune stale ids if items list refreshes
    useEffect(() => {
        setSelectedIds((prev) => {
            const valid = new Set(items.map((i) => i.id));
            const next = new Set([...prev].filter((id) => valid.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [items]);

    const toggleOne = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleAll = () => {
        setSelectedIds((prev) => {
            if (prev.size === items.length) return new Set();
            return new Set(items.map((i) => i.id));
        });
    };

    const exportCsv = () => {
        if (items.length === 0) {
            toast.error("No creatives to export.");
            return;
        }
        const rows = items;
        downloadCsv(
            `creatives-${campaignId.slice(0, 8)}.csv`,
            [
                { key: "name", label: "Name" },
                { key: "kind", label: "Kind" },
                { key: "media_type", label: "Media type", value: (r) => r.media_type || "" },
                { key: "file_id", label: "File ID", value: (r) => r.file_id || "" },
                { key: "impression_tracker_url", label: "Impression tracker" },
                { key: "click_tracker_url", label: "Click tracker" },
                { key: "html_code", label: "HTML code", value: (r) => (r.html_code ? `${r.html_code.length} chars` : "") },
                { key: "notes", label: "Notes" },
                { key: "created_by_name", label: "Created by" },
                { key: "created_at", label: "Created at" },
            ],
            rows,
        );
        toast.success(`Exported ${rows.length} creative${rows.length === 1 ? "" : "s"} to CSV.`);
    };

    const downloadZip = async () => {
        if (selectedIds.size === 0) {
            toast.error("Select at least one creative.");
            return;
        }
        setZipBusy(true);
        try {
            const resp = await api.post(
                `/campaigns/${campaignId}/creatives/download-zip`,
                { ids: [...selectedIds] },
                { responseType: "blob" },
            );
            const blob = new Blob([resp.data], { type: "application/zip" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const disp = resp.headers?.["content-disposition"] || "";
            const match = /filename="?([^"]+)"?/.exec(disp);
            a.download = match ? match[1] : `creatives-${campaignId.slice(0, 8)}.zip`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 2000);
            toast.success(`Downloaded ${selectedIds.size} creative${selectedIds.size === 1 ? "" : "s"} as ZIP.`);
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally {
            setZipBusy(false);
        }
    };

    const load = async () => {
        try {
            const { data } = await api.get(`/campaigns/${campaignId}/creatives`);
            setItems(data);
        } catch (e) {
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [campaignId]);

    const onRemove = async (id) => {
        const item = items.find((c) => c.id === id);
        const ok = await confirm({
            title: "Remove this creative?",
            description: item?.name ? `"${item.name}" will be removed from this campaign.` : "This creative will be removed from the campaign.",
            confirmText: "Remove",
            destructive: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/campaigns/${campaignId}/creatives/${id}`);
            toast.success("Creative removed");
            load();
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <div className="overline">Assets & trackers</div>
                    <h3 className="font-display text-xl">Creatives</h3>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {items.length > 0 && (
                        <>
                            <Button
                                onClick={downloadZip}
                                variant="outline"
                                disabled={selectedIds.size === 0 || zipBusy}
                                data-testid="creative-download-zip"
                                className="rounded-none border-slate-300 gap-2 h-9"
                            >
                                <DownloadSimple size={14} weight="bold" />
                                {zipBusy ? "Zipping…" : `Download ZIP${selectedIds.size ? ` (${selectedIds.size})` : ""}`}
                            </Button>
                            <Button
                                onClick={exportCsv}
                                variant="outline"
                                data-testid="creative-export-csv"
                                className="rounded-none border-slate-300 gap-2 h-9"
                            >
                                <FileCsv size={14} weight="bold" /> Export CSV
                            </Button>
                            <div className="inline-flex border border-slate-300 h-9" data-testid="creative-view-toggle">
                                <button
                                    onClick={() => setViewMode("table")}
                                    aria-pressed={viewMode === "table"}
                                    className={`px-2.5 flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider ${viewMode === "table" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                                    data-testid="creative-view-table"
                                >
                                    <Rows size={12} weight="bold" /> Table
                                </button>
                                <button
                                    onClick={() => setViewMode("grid")}
                                    aria-pressed={viewMode === "grid"}
                                    className={`px-2.5 flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider border-l border-slate-300 ${viewMode === "grid" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                                    data-testid="creative-view-grid"
                                >
                                    <SquaresFour size={12} weight="bold" /> Grid
                                </button>
                            </div>
                        </>
                    )}
                    {canEdit && (
                        <>
                            <Button
                                onClick={() => setOpenBulk(true)}
                                variant="outline"
                                data-testid="creative-bulk-button"
                                className="rounded-none border-slate-300 gap-2 h-9"
                            >
                                <UploadSimple size={14} weight="bold" /> Bulk upload
                            </Button>
                            <Button
                                onClick={() => setOpenAdd(true)}
                                data-testid="creative-add-button"
                                className="rounded-none bg-slate-900 hover:bg-slate-800 text-white gap-2 h-9"
                            >
                                <Plus size={14} weight="bold" /> Add creative
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {loading && <p className="text-xs font-mono text-slate-400">loading…</p>}

            {!loading && items.length === 0 && (
                <div className="border border-dashed border-slate-300 p-12 text-center" data-testid="creatives-empty">
                    <p className="text-sm text-slate-500">No creatives yet — upload an image / video / audio file, or add a tracker-only entry.</p>
                </div>
            )}

            {!loading && items.length > 0 && viewMode === "grid" && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="creatives-list">
                    {items.map((c) => (
                        <CreativeCard
                            key={c.id}
                            c={c}
                            canEdit={canEdit}
                            selected={selectedIds.has(c.id)}
                            onToggle={() => toggleOne(c.id)}
                            onRemove={() => onRemove(c.id)}
                            onEdit={() => setEditingCreative(c)}
                            onPreview={() => setPreviewing(c)}
                        />
                    ))}
                </div>
            )}

            {!loading && items.length > 0 && viewMode === "table" && (
                <CreativesTable
                    items={items}
                    canEdit={canEdit}
                    selectedIds={selectedIds}
                    onToggleAll={toggleAll}
                    onToggleOne={toggleOne}
                    onEdit={setEditingCreative}
                    onRemove={onRemove}
                    onPreview={setPreviewing}
                />
            )}

            {openAdd && (
                <AddCreativeModal
                    campaignId={campaignId}
                    onClose={() => setOpenAdd(false)}
                    onAdded={() => { setOpenAdd(false); load(); }}
                />
            )}

            {openBulk && (
                <BulkAddCreativesModal
                    campaignId={campaignId}
                    onClose={() => setOpenBulk(false)}
                    onAdded={() => { setOpenBulk(false); load(); }}
                />
            )}

            {editingCreative && (
                <EditCreativeModal
                    campaignId={campaignId}
                    creative={editingCreative}
                    onClose={() => setEditingCreative(null)}
                    onSaved={() => { setEditingCreative(null); load(); }}
                />
            )}

            {previewing && (
                <PreviewCreativeModal
                    creative={previewing}
                    onClose={() => setPreviewing(null)}
                />
            )}
        </div>
    );
}

function CreativeCard({ c, canEdit, onRemove, onEdit, onPreview, selected, onToggle }) {
    const isTrackerOnly = c.kind === "tracker_only";
    const [renderHtml, setRenderHtml] = useState(false);
    return (
        <div className="bg-white border border-slate-200 relative" data-testid={`creative-card-${c.id}`}>
            <label className="absolute top-2 left-2 z-10 bg-white border border-slate-300 p-1 cursor-pointer">
                <input
                    type="checkbox"
                    checked={!!selected}
                    onChange={onToggle}
                    className="cursor-pointer"
                    data-testid={`creative-card-select-${c.id}`}
                />
            </label>
            <div className="aspect-video bg-slate-50 border-b border-slate-200 flex items-center justify-center overflow-hidden">
                {c.file_id ? (
                    <CreativePreview fileId={c.file_id} mediaType={c.media_type} name={c.name} />
                ) : c.html_code && renderHtml ? (
                    <iframe
                        title={`html-preview-${c.id}`}
                        srcDoc={c.html_code}
                        sandbox="allow-scripts allow-same-origin"
                        className="w-full h-full bg-white"
                        data-testid={`creative-html-iframe-${c.id}`}
                    />
                ) : c.html_code ? (
                    <div className="text-center px-4">
                        <Code size={28} className="text-slate-400 mx-auto" />
                        <div className="overline mt-2 text-slate-500">HTML / rich media</div>
                    </div>
                ) : (
                    <div className="text-center px-4">
                        <LinkIcon size={28} className="text-slate-400 mx-auto" />
                        <div className="overline mt-2 text-slate-500">Tracker-only</div>
                    </div>
                )}
            </div>
            <div className="p-4">
                <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="min-w-0">
                        <div className="font-medium text-sm truncate">{c.name}</div>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            {c.media_type && (
                                <span className="font-mono text-[10px] uppercase tracking-wider bg-slate-100 text-slate-700 px-1.5 py-0.5">{c.media_type}</span>
                            )}
                            {isTrackerOnly && !c.html_code && (
                                <span className="font-mono text-[10px] uppercase tracking-wider bg-[var(--c-vermilion)] text-white px-1.5 py-0.5">tracker</span>
                            )}
                            {c.html_code && (
                                <span className="font-mono text-[10px] uppercase tracking-wider bg-slate-900 text-white px-1.5 py-0.5">HTML</span>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-0.5 shrink-0">
                        <button
                            onClick={onPreview}
                            data-testid={`creative-card-preview-${c.id}`}
                            className="p-1.5 text-slate-400 hover:text-slate-900"
                            title="Preview"
                        >
                            <Eye size={14} />
                        </button>
                        {c.file_id && (
                            <a
                                href={`${API}/files/${c.file_id}/download`}
                                data-testid={`creative-card-download-${c.id}`}
                                className="p-1.5 text-slate-400 hover:text-slate-900"
                                title="Download"
                            >
                                <DownloadSimple size={14} />
                            </a>
                        )}
                        {canEdit && (
                            <>
                                <button
                                    onClick={onEdit}
                                    data-testid={`creative-edit-${c.id}`}
                                    className="p-1.5 text-slate-400 hover:text-slate-900"
                                    title="Edit"
                                >
                                    <PencilSimple size={14} />
                                </button>
                                <button
                                    onClick={onRemove}
                                    data-testid={`creative-remove-${c.id}`}
                                    className="p-1.5 text-slate-400 hover:text-red-700"
                                    title="Remove"
                                >
                                    <Trash size={14} />
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {c.html_code && (
                    <div className="border-t border-slate-100 pt-2">
                        <div className="flex items-center justify-between mb-1">
                            <span className="overline" style={{ fontSize: 9 }}>HTML snippet</span>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={() => setRenderHtml((v) => !v)}
                                    data-testid={`creative-html-toggle-${c.id}`}
                                    className="text-[10px] font-mono uppercase tracking-wider text-slate-600 hover:text-slate-900 px-1.5 py-0.5 inline-flex items-center gap-1"
                                >
                                    <Eye size={11} />{renderHtml ? "code" : "preview"}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        navigator.clipboard?.writeText(c.html_code);
                                        toast.success("HTML copied");
                                    }}
                                    data-testid={`creative-html-copy-${c.id}`}
                                    className="text-[10px] font-mono uppercase tracking-wider text-[var(--c-accent)] hover:underline px-1.5 py-0.5 inline-flex items-center gap-1"
                                >
                                    <Copy size={11} /> copy
                                </button>
                            </div>
                        </div>
                        <pre className="text-[10px] font-mono bg-slate-50 border border-slate-200 p-2 max-h-32 overflow-auto whitespace-pre-wrap break-all leading-relaxed">
                            {c.html_code}
                        </pre>
                    </div>
                )}

                {(c.impression_tracker_url || c.click_tracker_url) && (
                    <div className="border-t border-slate-100 pt-2 space-y-1 mt-2">
                        {c.impression_tracker_url && (
                            <TrackerLine label="impression" url={c.impression_tracker_url} testid={`imp-tracker-${c.id}`} />
                        )}
                        {c.click_tracker_url && (
                            <TrackerLine label="click" url={c.click_tracker_url} testid={`click-tracker-${c.id}`} />
                        )}
                    </div>
                )}

                {c.notes && (
                    <p className="text-xs text-slate-500 mt-2 line-clamp-2">{c.notes}</p>
                )}

                <div className="overline text-slate-400 mt-3">added {fmtDateTime(c.created_at)}</div>
            </div>
        </div>
    );
}

function TrackerLine({ label, url, testid }) {
    return (
        <div className="flex items-center gap-2" data-testid={testid}>
            <span className="font-mono text-[9px] uppercase tracking-wider text-slate-500 w-16 shrink-0">{label}</span>
            <a
                href={url}
                target="_blank"
                rel="noreferrer noopener"
                className="font-mono text-[10px] text-[var(--c-accent)] hover:underline truncate flex-1"
                title={url}
            >
                {url}
            </a>
            <ArrowUpRight size={10} className="text-slate-400 shrink-0" />
        </div>
    );
}

function CreativePreview({ fileId, mediaType, name }) {
    const [src, setSrc] = useState(null);
    const [err, setErr] = useState(false);

    useEffect(() => {
        let revokedUrl = null;
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
                if (cancelled) return;
                const url = URL.createObjectURL(res.data);
                revokedUrl = url;
                setSrc(url);
            } catch (e) {
                if (!cancelled) setErr(true);
            }
        })();
        return () => {
            cancelled = true;
            if (revokedUrl) URL.revokeObjectURL(revokedUrl);
        };
    }, [fileId]);

    if (err) {
        return <div className="text-xs font-mono text-slate-400">preview unavailable</div>;
    }
    if (!src) {
        return <div className="text-xs font-mono text-slate-400">loading…</div>;
    }
    if (mediaType === "video") {
        return (
            <video
                src={src}
                controls
                className="w-full h-full object-contain bg-black"
                data-testid="creative-video-preview"
            />
        );
    }
    if (mediaType === "audio") {
        return (
            <div className="p-4 w-full">
                <SpeakerHigh size={32} className="text-slate-400 mx-auto mb-2" />
                <audio src={src} controls className="w-full" data-testid="creative-audio-preview" />
            </div>
        );
    }
    return (
        <img
            src={src}
            alt={name || "creative"}
            className="w-full h-full object-contain"
            data-testid="creative-image-preview"
        />
    );
}

function AddCreativeModal({ campaignId, onClose, onAdded }) {
    const [mode, setMode] = useState("creative"); // "creative" (file + optional trackers) | "tracker_only"
    const [mediaType, setMediaType] = useState("image");
    const [file, setFile] = useState(null);
    const [form, setForm] = useState({
        name: "",
        impression_tracker_url: "",
        click_tracker_url: "",
        html_code: "",
        notes: "",
    });
    const [busy, setBusy] = useState(false);

    const accept = MEDIA_TYPES.find((m) => m.value === mediaType)?.accept || "image/*,video/*,audio/*";

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            toast.error("Give the creative a name.");
            return;
        }
        if (mode === "creative" && !file) {
            toast.error("Upload a file or switch to tracker-only mode.");
            return;
        }
        if (mode === "tracker_only" &&
            !form.impression_tracker_url.trim() &&
            !form.click_tracker_url.trim() &&
            !form.html_code.trim()) {
            toast.error("Add at least an HTML snippet, impression tracker, or click tracker.");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/campaigns/${campaignId}/creatives`, {
                name: form.name.trim(),
                media_type: mode === "creative" ? mediaType : null,
                file_id: mode === "creative" ? file?.id : null,
                impression_tracker_url: form.impression_tracker_url.trim() || null,
                click_tracker_url: form.click_tracker_url.trim() || null,
                html_code: mode === "tracker_only" ? (form.html_code.trim() || null) : null,
                notes: form.notes.trim() || null,
            });
            toast.success("Creative added");
            onAdded?.();
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="creative-modal">
            <form onSubmit={submit} className="bg-white border border-slate-200 max-w-2xl w-full mt-12 mb-12">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
                    <div>
                        <div className="overline">Add</div>
                        <h3 className="font-display text-lg">Creative or tracker</h3>
                    </div>
                    <button type="button" onClick={onClose} className="text-2xl leading-none text-slate-500" data-testid="creative-modal-close">×</button>
                </div>

                <div className="p-6 space-y-5">
                    {/* Mode tabs */}
                    <div className="flex gap-px bg-slate-200 border border-slate-200" data-testid="creative-mode-tabs">
                        {[
                            { value: "creative", label: "File + trackers", hint: "Upload image / video / audio" },
                            { value: "tracker_only", label: "Tracker only", hint: "Submit impression / click URLs" },
                        ].map((t) => (
                            <button
                                key={t.value}
                                type="button"
                                onClick={() => setMode(t.value)}
                                data-testid={`creative-mode-${t.value}`}
                                className={`flex-1 px-3 py-2.5 text-left ${mode === t.value ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-50"}`}
                            >
                                <div className="text-xs font-medium">{t.label}</div>
                                <div className={`text-[10px] font-mono mt-0.5 ${mode === t.value ? "text-slate-300" : "text-slate-500"}`}>
                                    {t.hint}
                                </div>
                            </button>
                        ))}
                    </div>

                    <Field label="Creative name">
                        <Input
                            data-testid="creative-name"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="rounded-none border-slate-300"
                            placeholder="e.g. Hero Video 15s — V1"
                            required
                        />
                    </Field>

                    {mode === "creative" && (
                        <>
                            <Field label="Media type">
                                <div className="flex gap-px bg-slate-200 border border-slate-200" data-testid="creative-media-type">
                                    {MEDIA_TYPES.map((m) => {
                                        const Icon = m.Icon;
                                        const active = mediaType === m.value;
                                        return (
                                            <button
                                                key={m.value}
                                                type="button"
                                                onClick={() => { setMediaType(m.value); setFile(null); }}
                                                data-testid={`media-${m.value}`}
                                                className={`flex-1 px-3 py-2 inline-flex items-center justify-center gap-2 ${active ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-50"}`}
                                            >
                                                <Icon size={14} />
                                                <span className="text-xs font-medium">{m.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </Field>
                            <Field label="File">
                                <FileUploader
                                    value={file}
                                    onChange={setFile}
                                    accept={accept}
                                    maxSizeMb={mediaType === "video" ? 200 : 50}
                                    testid="creative-file"
                                />
                            </Field>
                        </>
                    )}

                    <Field label="Impression tracker URL (optional)">
                        <Input
                            data-testid="creative-imp-tracker"
                            value={form.impression_tracker_url}
                            onChange={(e) => setForm({ ...form, impression_tracker_url: e.target.value })}
                            placeholder="https://…"
                            className="rounded-none border-slate-300 font-mono"
                        />
                    </Field>
                    <Field label="Click tracker URL (optional)">
                        <Input
                            data-testid="creative-click-tracker"
                            value={form.click_tracker_url}
                            onChange={(e) => setForm({ ...form, click_tracker_url: e.target.value })}
                            placeholder="https://…"
                            className="rounded-none border-slate-300 font-mono"
                        />
                    </Field>
                    {mode === "tracker_only" && (
                        <Field label="HTML / rich-media code (optional)">
                            <Textarea
                                data-testid="creative-html-code"
                                value={form.html_code}
                                onChange={(e) => setForm({ ...form, html_code: e.target.value })}
                                placeholder="<!-- paste DCM tag, VAST XML, or rich-media HTML here -->"
                                rows={6}
                                className="rounded-none border-slate-300 font-mono text-[11px]"
                            />
                            <div className="overline mt-1 text-slate-400" style={{ fontSize: 9 }}>
                                Pasted code is shown as raw text by default. Use the &ldquo;Render preview&rdquo; toggle on the creative card to view it sandboxed.
                            </div>
                        </Field>
                    )}
                    <Field label="Notes (optional)">
                        <Textarea
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            rows={2}
                            className="rounded-none border-slate-300"
                        />
                    </Field>
                </div>

                <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-slate-300">Cancel</Button>
                    <Button
                        type="submit"
                        disabled={busy}
                        data-testid="creative-submit"
                        className="rounded-none bg-slate-900 hover:bg-slate-800 text-white px-6"
                    >
                        {busy ? "Saving…" : "Add"}
                    </Button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="overline mb-1.5 block">{label}</Label>
            {children}
        </div>
    );
}


function BulkAddCreativesModal({ campaignId, onClose, onAdded }) {
    const [uploaded, setUploaded] = useState([]); // [{id, filename, size, content_type}]
    const [busy, setBusy] = useState(false);

    const guessMediaType = (contentType = "") => {
        if (contentType.startsWith("image/")) return "image";
        if (contentType.startsWith("video/")) return "video";
        if (contentType.startsWith("audio/")) return "audio";
        return null;
    };

    const handleUploaded = (files) => {
        setUploaded((prev) => [...prev, ...files]);
    };

    const removeStaged = (idx) => {
        setUploaded((prev) => prev.filter((_, i) => i !== idx));
    };

    const submit = async () => {
        if (uploaded.length === 0) {
            toast.error("Upload at least one file first.");
            return;
        }
        setBusy(true);
        try {
            const items = uploaded.map((f) => ({
                name: f.filename?.replace(/\.[^/.]+$/, "") || "Untitled",
                media_type: guessMediaType(f.content_type),
                file_id: f.id,
            }));
            const { data } = await api.post(`/campaigns/${campaignId}/creatives/bulk`, { items });
            toast.success(`Added ${data.created} creative${data.created === 1 ? "" : "s"}`);
            onAdded?.();
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="creative-bulk-modal">
            <div className="bg-white border border-slate-200 max-w-2xl w-full mt-12 mb-12">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
                    <div>
                        <div className="overline">Bulk</div>
                        <h3 className="font-display text-lg">Upload multiple creatives</h3>
                    </div>
                    <button type="button" onClick={onClose} className="text-2xl leading-none text-slate-500" data-testid="creative-bulk-close">×</button>
                </div>
                <div className="p-6 space-y-4">
                    <Field label="Drop files or click to select — one creative per file">
                        <FileUploader
                            multiple
                            onMultiUploaded={handleUploaded}
                            accept="image/*,video/*,audio/*"
                            maxSizeMb={200}
                            testid="creative-bulk-uploader"
                        />
                    </Field>
                    {uploaded.length > 0 && (
                        <div className="border border-slate-200 divide-y divide-slate-200" data-testid="creative-bulk-staged">
                            <div className="px-3 py-2 bg-slate-50 flex items-center justify-between">
                                <span className="overline">Ready to add ({uploaded.length})</span>
                                <button
                                    type="button"
                                    onClick={() => setUploaded([])}
                                    className="text-[10px] font-mono uppercase tracking-wider text-slate-500 hover:text-red-700"
                                    data-testid="creative-bulk-clear"
                                >
                                    Clear all
                                </button>
                            </div>
                            {uploaded.map((f, idx) => (
                                <div key={`${f.id}-${idx}`} className="px-3 py-2 flex items-center justify-between gap-2 text-sm">
                                    <div className="min-w-0">
                                        <div className="truncate font-medium">{f.filename}</div>
                                        <div className="font-mono text-[10px] text-slate-500">
                                            {guessMediaType(f.content_type) || "unknown"} · {Math.round((f.size || 0) / 1024)} KB
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeStaged(idx)}
                                        className="p-1 text-slate-400 hover:text-red-700"
                                        data-testid={`creative-bulk-remove-${idx}`}
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    <p className="text-[11px] text-slate-500">
                        Each file becomes a separate creative. Media type is inferred from the file type; you can edit
                        names, trackers, and notes on individual creatives after creation.
                    </p>
                </div>
                <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-slate-300">Cancel</Button>
                    <Button
                        type="button"
                        onClick={submit}
                        disabled={busy || uploaded.length === 0}
                        data-testid="creative-bulk-submit"
                        className="rounded-none bg-slate-900 hover:bg-slate-800 text-white px-6"
                    >
                        {busy ? "Adding…" : `Add ${uploaded.length || ""} creative${uploaded.length === 1 ? "" : "s"}`.trim()}
                    </Button>
                </div>
            </div>
        </div>
    );
}


function EditCreativeModal({ campaignId, creative, onClose, onSaved }) {
    const [form, setForm] = useState({
        name: creative.name || "",
        media_type: creative.media_type || "",
        impression_tracker_url: creative.impression_tracker_url || "",
        click_tracker_url: creative.click_tracker_url || "",
        html_code: creative.html_code || "",
        notes: creative.notes || "",
    });
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            toast.error("Give the creative a name.");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                name: form.name.trim(),
                media_type: form.media_type || null,
                impression_tracker_url: form.impression_tracker_url.trim() || null,
                click_tracker_url: form.click_tracker_url.trim() || null,
                html_code: form.html_code.trim() || null,
                notes: form.notes.trim() || null,
            };
            await api.patch(`/campaigns/${campaignId}/creatives/${creative.id}`, payload);
            toast.success("Creative updated");
            onSaved?.();
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="creative-edit-modal">
            <form onSubmit={submit} className="bg-white border border-slate-200 max-w-2xl w-full mt-12 mb-12">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
                    <div>
                        <div className="overline">Edit</div>
                        <h3 className="font-display text-lg">{creative.name}</h3>
                    </div>
                    <button type="button" onClick={onClose} className="text-2xl leading-none text-slate-500" data-testid="creative-edit-close">×</button>
                </div>
                <div className="p-6 space-y-5">
                    <Field label="Creative name">
                        <Input
                            data-testid="creative-edit-name"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="rounded-none border-slate-300"
                            required
                        />
                    </Field>
                    {creative.file_id && (
                        <Field label="Media type">
                            <Select value={form.media_type || ""} onValueChange={(v) => setForm({ ...form, media_type: v })}>
                                <SelectTrigger data-testid="creative-edit-media-type" className="rounded-none h-10 border-slate-300">
                                    <SelectValue placeholder="Select…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {MEDIA_TYPES.map((m) => (
                                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                    )}
                    <Field label="Impression tracker URL">
                        <Input
                            data-testid="creative-edit-imp-tracker"
                            value={form.impression_tracker_url}
                            onChange={(e) => setForm({ ...form, impression_tracker_url: e.target.value })}
                            placeholder="https://…"
                            className="rounded-none border-slate-300 font-mono"
                        />
                    </Field>
                    <Field label="Click tracker URL">
                        <Input
                            data-testid="creative-edit-click-tracker"
                            value={form.click_tracker_url}
                            onChange={(e) => setForm({ ...form, click_tracker_url: e.target.value })}
                            placeholder="https://…"
                            className="rounded-none border-slate-300 font-mono"
                        />
                    </Field>
                    {creative.kind === "tracker_only" && (
                        <Field label="HTML / rich-media code">
                            <Textarea
                                data-testid="creative-edit-html-code"
                                value={form.html_code}
                                onChange={(e) => setForm({ ...form, html_code: e.target.value })}
                                rows={6}
                                className="rounded-none border-slate-300 font-mono text-[11px]"
                            />
                        </Field>
                    )}
                    <Field label="Notes">
                        <Textarea
                            data-testid="creative-edit-notes"
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            rows={2}
                            className="rounded-none border-slate-300"
                        />
                    </Field>
                </div>
                <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-slate-300">Cancel</Button>
                    <Button
                        type="submit"
                        disabled={busy}
                        data-testid="creative-edit-submit"
                        className="rounded-none bg-slate-900 hover:bg-slate-800 text-white px-6"
                    >
                        {busy ? "Saving…" : "Save changes"}
                    </Button>
                </div>
            </form>
        </div>
    );
}


// ---------------------------------------------------------------------------
// Table view
// ---------------------------------------------------------------------------
function CreativesTable({ items, canEdit, selectedIds, onToggleAll, onToggleOne, onEdit, onRemove, onPreview }) {
    const allSelected = items.length > 0 && selectedIds.size === items.length;
    const trunc = (s, n = 40) => (!s ? "—" : s.length > n ? s.slice(0, n - 1) + "…" : s);
    return (
        <div className="border border-slate-200 overflow-x-auto" data-testid="creatives-table">
            <table className="w-full text-sm">
                <thead className="bg-slate-50">
                    <tr>
                        <th className="w-8 px-3 py-2">
                            <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={onToggleAll}
                                data-testid="creative-table-select-all"
                                aria-label="Select all creatives"
                            />
                        </th>
                        <th className="text-left px-3 py-2 overline">Name</th>
                        <th className="text-left px-3 py-2 overline">Type</th>
                        <th className="text-left px-3 py-2 overline">Kind</th>
                        <th className="text-left px-3 py-2 overline">Impression tracker</th>
                        <th className="text-left px-3 py-2 overline">Click tracker</th>
                        <th className="text-left px-3 py-2 overline">Notes</th>
                        <th className="text-left px-3 py-2 overline">Created by</th>
                        <th className="text-left px-3 py-2 overline whitespace-nowrap">Created at</th>
                        <th className="text-right px-3 py-2 overline">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((c) => {
                        const checked = selectedIds.has(c.id);
                        return (
                            <tr
                                key={c.id}
                                className={`border-t border-slate-200 ${checked ? "bg-slate-50" : "hover:bg-slate-50/60"}`}
                                data-testid={`creative-row-${c.id}`}
                            >
                                <td className="px-3 py-2 align-top">
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => onToggleOne(c.id)}
                                        data-testid={`creative-select-${c.id}`}
                                        aria-label={`Select ${c.name}`}
                                    />
                                </td>
                                <td className="px-3 py-2 align-top">
                                    <div className="font-medium truncate max-w-[240px]" title={c.name}>{c.name}</div>
                                    {c.file_id && (
                                        <div className="font-mono text-[10px] text-slate-500 mt-0.5">file · {c.file_id.slice(0, 8)}</div>
                                    )}
                                </td>
                                <td className="px-3 py-2 align-top">
                                    {c.media_type ? (
                                        <span className="font-mono text-[10px] uppercase tracking-wider bg-slate-100 text-slate-700 px-1.5 py-0.5">{c.media_type}</span>
                                    ) : (
                                        <span className="text-slate-400">—</span>
                                    )}
                                </td>
                                <td className="px-3 py-2 align-top">
                                    <span className={`font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${c.kind === "tracker_only" ? "bg-[var(--c-vermilion)] text-white" : "bg-slate-900 text-white"}`}>
                                        {c.kind === "tracker_only" ? "tracker" : "creative"}
                                    </span>
                                </td>
                                <td className="px-3 py-2 align-top">
                                    {c.impression_tracker_url ? (
                                        <a
                                            href={c.impression_tracker_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="font-mono text-[11px] text-[var(--c-accent)] hover:underline break-all"
                                            title={c.impression_tracker_url}
                                            data-testid={`creative-table-imp-${c.id}`}
                                        >
                                            {trunc(c.impression_tracker_url, 32)}
                                        </a>
                                    ) : (
                                        <span className="text-slate-400">—</span>
                                    )}
                                </td>
                                <td className="px-3 py-2 align-top">
                                    {c.click_tracker_url ? (
                                        <a
                                            href={c.click_tracker_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="font-mono text-[11px] text-[var(--c-accent)] hover:underline break-all"
                                            title={c.click_tracker_url}
                                            data-testid={`creative-table-click-${c.id}`}
                                        >
                                            {trunc(c.click_tracker_url, 32)}
                                        </a>
                                    ) : (
                                        <span className="text-slate-400">—</span>
                                    )}
                                </td>
                                <td className="px-3 py-2 align-top max-w-[200px]">
                                    <div className="truncate text-slate-700" title={c.notes || ""}>{c.notes || "—"}</div>
                                </td>
                                <td className="px-3 py-2 align-top whitespace-nowrap text-slate-700">{c.created_by_name || "—"}</td>
                                <td className="px-3 py-2 align-top whitespace-nowrap font-mono text-[11px] text-slate-500">{c.created_at ? fmtDateTime(c.created_at) : "—"}</td>
                                <td className="px-3 py-2 align-top text-right whitespace-nowrap">
                                    <div className="inline-flex items-center gap-0.5">
                                        <button
                                            onClick={() => onPreview(c)}
                                            data-testid={`creative-preview-${c.id}`}
                                            className="p-1.5 text-slate-500 hover:text-slate-900"
                                            title="Preview"
                                        >
                                            <Eye size={14} />
                                        </button>
                                        {c.file_id && (
                                            <a
                                                href={`${API}/files/${c.file_id}/download`}
                                                data-testid={`creative-download-${c.id}`}
                                                className="p-1.5 text-slate-500 hover:text-slate-900"
                                                title="Download"
                                            >
                                                <DownloadSimple size={14} />
                                            </a>
                                        )}
                                        {canEdit && (
                                            <>
                                                <button
                                                    onClick={() => onEdit(c)}
                                                    data-testid={`creative-table-edit-${c.id}`}
                                                    className="p-1.5 text-slate-500 hover:text-slate-900"
                                                    title="Edit"
                                                >
                                                    <PencilSimple size={14} />
                                                </button>
                                                <button
                                                    onClick={() => onRemove(c.id)}
                                                    data-testid={`creative-table-remove-${c.id}`}
                                                    className="p-1.5 text-slate-500 hover:text-red-700"
                                                    title="Remove"
                                                >
                                                    <Trash size={14} />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}


// ---------------------------------------------------------------------------
// Preview modal
// ---------------------------------------------------------------------------
function PreviewCreativeModal({ creative, onClose }) {
    const c = creative;
    const fileUrl = c.file_id ? `${API}/files/${c.file_id}/download` : null;
    return (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="creative-preview-modal">
            <div className="bg-white border border-slate-200 max-w-3xl w-full mt-12 mb-12">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
                    <div className="min-w-0">
                        <div className="overline">Preview</div>
                        <h3 className="font-display text-lg truncate">{c.name}</h3>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {fileUrl && (
                            <a
                                href={fileUrl}
                                className="text-xs font-mono uppercase tracking-wider text-slate-700 hover:text-slate-900 border border-slate-300 px-3 py-1.5 inline-flex items-center gap-1"
                                data-testid="creative-preview-download"
                            >
                                <DownloadSimple size={12} weight="bold" /> Download
                            </a>
                        )}
                        <button
                            type="button"
                            onClick={onClose}
                            className="text-2xl leading-none text-slate-500"
                            data-testid="creative-preview-close"
                        >
                            ×
                        </button>
                    </div>
                </div>

                <div className="p-6 space-y-4">
                    <div className="bg-slate-50 border border-slate-200 flex items-center justify-center min-h-[240px] p-4">
                        {c.media_type === "image" && fileUrl && (
                            <img src={fileUrl} alt={c.name} className="max-h-[480px] max-w-full object-contain" />
                        )}
                        {c.media_type === "video" && fileUrl && (
                            <video src={fileUrl} controls className="max-h-[480px] max-w-full" />
                        )}
                        {c.media_type === "audio" && fileUrl && (
                            <audio src={fileUrl} controls className="w-full" />
                        )}
                        {!c.media_type && fileUrl && (
                            <div className="text-center">
                                <LinkIcon size={32} className="text-slate-400 mx-auto" />
                                <div className="mt-2 text-xs font-mono text-slate-500">Binary file — use Download to view</div>
                            </div>
                        )}
                        {c.html_code && !fileUrl && (
                            <iframe
                                title={`html-preview-modal-${c.id}`}
                                srcDoc={c.html_code}
                                sandbox="allow-scripts allow-same-origin"
                                className="w-full h-[400px] bg-white border border-slate-200"
                            />
                        )}
                        {!fileUrl && !c.html_code && (
                            <div className="text-center px-4">
                                <LinkIcon size={32} className="text-slate-400 mx-auto" />
                                <div className="overline mt-2 text-slate-500">Tracker-only creative</div>
                            </div>
                        )}
                    </div>

                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                        <MetaRow label="Kind" value={c.kind === "tracker_only" ? "Tracker-only" : "Creative with file"} />
                        <MetaRow label="Media type" value={c.media_type || "—"} />
                        <MetaRow label="Impression tracker" value={c.impression_tracker_url || "—"} linkify />
                        <MetaRow label="Click tracker" value={c.click_tracker_url || "—"} linkify />
                        <MetaRow label="Notes" value={c.notes || "—"} full />
                        <MetaRow label="Created by" value={c.created_by_name || "—"} />
                        <MetaRow label="Created at" value={c.created_at ? fmtDateTime(c.created_at) : "—"} />
                        {c.updated_at && <MetaRow label="Updated at" value={fmtDateTime(c.updated_at)} />}
                    </dl>
                </div>
            </div>
        </div>
    );
}

function MetaRow({ label, value, linkify, full }) {
    return (
        <div className={full ? "sm:col-span-2" : ""}>
            <dt className="overline mb-0.5">{label}</dt>
            <dd className="font-mono text-[12px] break-all text-slate-800">
                {linkify && value && value !== "—" ? (
                    <a href={value} target="_blank" rel="noreferrer" className="text-[var(--c-accent)] hover:underline">{value}</a>
                ) : (
                    value
                )}
            </dd>
        </div>
    );
}

